import type {
  ProductV2,
  Profile,
  CountryConfiguration,
  StoredPaymentMethod,
  BillingAddress,
  ProductsRequest,
  CheckoutAdyenSessionResponse,
  CheckoutSessionRequest,
  PurchasesUpdateDraftResponse,
  PurchasesUpdateDraftRequest,
  CheckoutAdyenPaymentRequest,
  CheckoutAdyenPaymentResponse,
  CheckoutAdyenPaymentDetailsRequest,
  CheckoutAdyenPaymentDetailsResponse,
  PurchasesGetResponse,
  Receipt,
  PurchaseGetDraftResponse,
  FetchReceiptsResponse,
  FetchReceiptsRequest,
  CheckoutRazerSessionResponse,
  RazerCheckoutSessionInputSchema,
  PurchasesGetPendingReponse,
  CheckoutXsollaSessionResponse,
  CountryCode,
} from '@common'
import { createApi, FetchArgs, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react'
import fetch from 'cross-fetch'

import { config } from '../config'
import { RootState } from './store'
import { isClient } from './utils/isClient'
import { useAppSelector } from './utils/useAppSelector'

const QUERY_TIMEOUT = 20000
const { API_URL } = config

export interface LoginConfirmRequest {
  email: string
  pin: string
}

const unRetryableErrors: (number | 'FETCH_ERROR' | 'PARSING_ERROR' | 'TIMEOUT_ERROR' | 'CUSTOM_ERROR')[] = [
  400, 401, 403, 404,
]

export const extractErrorMessageFromResponse = (error: unknown) => {
  if (error === null) {
    return
  }
  if (typeof error === 'object' && 'data' in error) {
    const data = error.data
    if (data !== null && typeof data === 'object' && 'message' in data) {
      return data.message
    }
  }
  return undefined
}

const baseQuery = retry(
  async (args: string | FetchArgs, api, extraOptions) => {
    const result = await fetchBaseQuery({
      baseUrl: API_URL,
      fetchFn: isClient
        ? window.fetch
        : (request, init) => {
            if (typeof request === 'string') {
              return fetch(request, init)
            }
            return fetch(request.url, Object.assign({}, init, request))
          },
      prepareHeaders: (headers, { getState }) => {
        const token = (getState() as RootState).user.token
        if (token) {
          headers.set('authorization', `Bearer ${token}`)
        }
        return headers
      },
      timeout: QUERY_TIMEOUT,
    })(args, api, extraOptions)

    // Bail out if the error is not retryable
    if (result.error && unRetryableErrors.includes(result.error.status)) {
      retry.fail(result.error)
    }

    return result
  },
  { maxRetries: 3 },
)

export const api = createApi({
  baseQuery,
  tagTypes: ['User', 'Session', 'Draft'],
  endpoints: (builder) => ({
    login: builder.mutation<Record<string, unknown>, { email: string; lang: string }>({
      query: ({ email, lang }) => ({
        url: 'customers/login',
        method: 'POST',
        body: {
          email,
        },
        params: {
          lang,
        },
      }),
      extraOptions: {
        maxRetries: 0,
      },
    }),
    loginConfirm: builder.mutation<{ token: string }, LoginConfirmRequest>({
      query: (body) => ({
        url: 'customers/login/confirm',
        method: 'POST',
        body,
      }),
      extraOptions: {
        maxRetries: 0,
      },
    }),
    fetchProfile: builder.query<Profile, string>({
      query: (lang: string) => ({
        url: 'customers/me',
        method: 'GET',
        params: {
          lang,
        },
      }),
      providesTags: [{ type: 'User' }],
    }),
    fetchPurchase: builder.query<PurchasesGetResponse, string>({
      query: (id: string) => ({
        url: `purchases/v3/${id}`,
        method: 'GET',
      }),
      providesTags: [{ type: 'User' }],
    }),
    fetchPendingPurchases: builder.query<PurchasesGetPendingReponse[], void>({
      query: () => ({
        url: `purchases/v3/pending`,
        method: 'GET',
      }),
      providesTags: [{ type: 'User' }],
    }),
    fetchReceipt: builder.query<Receipt, string>({
      query: (id: string) => ({
        url: `receipts/${id}`,
        method: 'GET',
      }),
      providesTags: [{ type: 'User' }],
    }),
    fetchReceipts: builder.query<FetchReceiptsResponse, FetchReceiptsRequest>({
      query: (params) => ({
        url: `receipts`,
        method: 'GET',
        params,
      }),
      providesTags: [{ type: 'User' }],
    }),
    getDraft: builder.query<PurchaseGetDraftResponse, void>({
      query: (body) => ({
        url: 'purchases/v3/draft',
        method: 'GET',
        body,
      }),
      providesTags: [{ type: 'User' }, { type: 'Draft' }],
      serializeQueryArgs: () => 'draft',
      keepUnusedDataFor: 10,
      extraOptions: {
        maxRetries: 0,
      },
    }),
    updateDraft: builder.mutation<PurchasesUpdateDraftResponse, PurchasesUpdateDraftRequest>({
      query: (body) => ({
        url: 'purchases/v3/draft',
        method: 'PUT',
        body,
      }),
      extraOptions: {
        maxRetries: 0,
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedDraft } = await queryFulfilled
          dispatch(
            // Call the updateQueryData method with the correct query ID and the updater function
            // Note that cache id is always "draft" for this query
            api.util.updateQueryData('getDraft', undefined, () => updatedDraft),
          )
        } catch {}
      },
    }),
    checkoutAdyenSession: builder.mutation<CheckoutAdyenSessionResponse, CheckoutSessionRequest>({
      query: (body) => ({
        url: 'checkout/adyen/session',
        method: 'POST',
        body,
      }),
      extraOptions: {
        maxRetries: 0,
      },
    }),
    checkoutXsollaSession: builder.mutation<CheckoutXsollaSessionResponse, CheckoutSessionRequest>({
      query: (body) => ({
        url: 'checkout/xsolla/session',
        method: 'POST',
        body,
      }),
      extraOptions: {
        maxRetries: 0,
      },
    }),
    checkoutAdyenPayment: builder.mutation<CheckoutAdyenPaymentResponse, CheckoutAdyenPaymentRequest>({
      query: (body) => ({
        url: 'checkout/adyen/payment',
        method: 'POST',
        body,
      }),
      extraOptions: {
        maxRetries: 0,
      },
    }),
    checkoutRazerSession: builder.query<CheckoutRazerSessionResponse, RazerCheckoutSessionInputSchema>({
      query: (body) => ({
        url: 'checkout/razer/payment',
        method: 'POST',
        body,
      }),
      providesTags: [{ type: 'User' }, { type: 'Session' }, { type: 'Draft' }],
      extraOptions: {
        maxRetries: 0,
      },
    }),
    checkoutAdyenPaymentDetails: builder.mutation<
      CheckoutAdyenPaymentDetailsResponse,
      CheckoutAdyenPaymentDetailsRequest
    >({
      query: (body) => ({
        url: 'checkout/adyen/payment/details',
        method: 'POST',
        body,
      }),
      extraOptions: {
        maxRetries: 0,
      },
    }),
    fetchMarkets: builder.query<CountryConfiguration[], void>({
      query: () => ({
        url: 'markets',
        method: 'GET',
      }),
    }),
    fetchMarket: builder.query<CountryConfiguration, CountryCode>({
      query: (countryCode) => ({
        url: 'market',
        method: 'GET',
        params: {
          countryCode,
        },
      }),
    }),
    fetchProducts: builder.query<ProductV2[], ProductsRequest>({
      query: (params) => ({
        url: `products`,
        method: 'GET',
        params,
      }),
      providesTags: [{ type: 'User' }],
    }),
    removePaymentMethod: builder.mutation<unknown, string>({
      query: (id: string) => ({
        method: 'DELETE',
        url: `/customers/me/paymentMethods/${id}`,
      }),
    }),
    fetchStoredPaymentMethods: builder.query<StoredPaymentMethod[], void>({
      query: () => ({
        url: '/customers/me/paymentMethods',
        method: 'GET',
      }),
      providesTags: [{ type: 'User' }],
    }),
    validateCreator: builder.query<{ isValid: boolean }, string>({
      query: (creatorBoostCode) => ({
        method: 'POST',
        url: '/validateCreator',
        body: {
          creatorBoostCode,
        },
        extraOptions: {
          maxRetries: 0,
        },
      }),
    }),
    customerAddBillingAddress: builder.mutation<undefined | { statusCode: number; body: string }, BillingAddress>({
      query: (body) => ({
        method: 'POST',
        url: '/customers/me/address',
        body: { ...body },
      }),
      extraOptions: {
        maxRetries: 0,
      },
    }),
    customerRemoveBillingAddress: builder.query<unknown, void>({
      query: () => ({
        method: 'DELETE',
        url: '/customers/me/address',
      }),
      extraOptions: {
        maxRetries: 0,
      },
    }),
  }),
})

export const {
  useLoginMutation,
  useLoginConfirmMutation,
  useFetchProfileQuery,
  useFetchStoredPaymentMethodsQuery,
  useFetchPurchaseQuery,
  useFetchPendingPurchasesQuery,
  useFetchMarketsQuery,
  useFetchMarketQuery,
  useGetDraftQuery,
  useCheckoutAdyenSessionMutation,
  useCheckoutXsollaSessionMutation,
  useValidateCreatorQuery,
  useLazyValidateCreatorQuery,
  useRemovePaymentMethodMutation,
  usePrefetch,
  useFetchProductsQuery,
  useCustomerAddBillingAddressMutation,
  useLazyCustomerRemoveBillingAddressQuery,
  useCheckoutAdyenPaymentMutation,
  useCheckoutAdyenPaymentDetailsMutation,
  useFetchReceiptQuery,
  useFetchReceiptsQuery,
  useLazyCheckoutRazerSessionQuery,
} = api

export const getDraftQuerySelector = api.endpoints.getDraft.select()
export const useFetchProfileQueryState = api.endpoints.fetchProfile.useQueryState
export const updateDraftSelector = api.endpoints.updateDraft.select('updateDraft')
export const useGetDraftQueryState = () => useAppSelector(getDraftQuerySelector)
export const useUpdateDraftMutationState = () => useAppSelector(updateDraftSelector)
